import "./destination-and-coverage.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { FaChevronLeft } from "react-icons/fa";
import RenderMap from "../../../landing-page/destinations-and-coverage/map/render-map";
import { useTranslation } from "react-i18next";

const VIEWS_WITH_STATION_OVERLAY = [
  "national",
  "northAmerica",
  "southAmerica",
  "asia",
  "europe",
];

function DestinationAndCoverage() {
  const [tStationsNationals] = useTranslation("stationsNationals");
  const [typeDestinationView, setTypeDestinationView] = useState("");
  const [nationalStations, setNationalStations] = useState([]);
  const [internationalStations, setInternationalStations] = useState({});
  const [nationalDetails, setNationalDetails] = useState({});

  const showStationsOverlay = VIEWS_WITH_STATION_OVERLAY.includes(
    typeDestinationView
  );

  useEffect(() => {
    getNationalStations();
    getInternationalStations();
  }, []);

  const getNationalStations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/stations?lng=es`,
        {
          headers: {
            authorization: process.env.REACT_APP_PUBLIC_KEY,
          },
        }
      );
      setNationalStations(response.data.stations);
    } catch (error) {
      console.error(error);
    }
  };

  const getInternationalStations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/international-stations?lng=es`,
        {
          headers: {
            authorization: process.env.REACT_APP_PUBLIC_KEY,
          },
        }
      );

      const regionGroup = response.data.stations.reduce((acc, item) => {
        let region = item.region;
        if (item.region === "Canada" || (item.region && item.region.startsWith("USA"))) {
          region = "northAmerica";
        } else if (
          item.region === "Caribe" ||
          item.region === "Centroamerica" ||
          item.region === "Sudamerica"
        ) {
          region = "southAmerica";
        } else if (item.region === "Europa") {
          region = "europe";
        } else if (item.region === "Asia") {
          region = "asia";
        }

        if (!acc[region]) {
          acc[region] = {};
        }

        const country = item.country;

        if (!acc[region][country]) {
          acc[region][country] = [];
        }

        acc[region][country].push(item);

        return acc;
      }, {});
      setInternationalStations(regionGroup);
    } catch (error) {
      console.error(error);
    }
  };

  const handleMouseEnter = (destination) => {
    let countries = [];
    if (destination === "national") {
      countries = document.querySelectorAll(".National");
    } else if (destination === "international") {
      countries = document.querySelectorAll(".International");
    } else if (destination === "america") {
      countries = document.querySelectorAll(".America");
    } else if (destination === "asia") {
      countries = document.querySelectorAll(".Asia");
    } else if (destination === "europe") {
      countries = document.querySelectorAll(".Europe");
    } else if (destination === "northAmerica") {
      countries = document.querySelectorAll(".NorthAmerica");
    } else if (destination === "southAmerica") {
      countries = document.querySelectorAll(".SouthAmerica");
    }

    countries.forEach((country) => {
      country.classList.add("active-color");
    });
  };

  const handleMouseLeave = () => {
    const countries = document.querySelectorAll(".active-color");
    countries.forEach((country) => {
      country.classList.remove("active-color");
    });
  };

  const resetToRootView = () => {
    setNationalDetails({});
    handleMouseLeave();
    setTypeDestinationView("");
  };

  useEffect(() => {
    if (!showStationsOverlay) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        resetToRootView();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showStationsOverlay]);

  const changeView = (view) => {
    if (view === "") {
      resetToRootView();
      return;
    }
    handleMouseLeave();
    handleMouseEnter(view);
    setTypeDestinationView(view);
  };

  const stationDetail = (station) => {
    setNationalDetails(station);
  };

  const renderDestination = (data) => (
    <>
      {data.btnReturn && (
        <button
          type="button"
          className="btn-return"
          onClick={() => changeView("")}
          aria-label={tStationsNationals(
            "destinations-and-coverage.back"
          )}
        >
          <FaChevronLeft aria-hidden />
          <span className="btn-return__label">
            {tStationsNationals("destinations-and-coverage.back")}
          </span>
        </button>
      )}

      <div className="dest-covera-section">
        <h4>{data.title}</h4>

        <div className="buttons">
          {data.btns.map((btn, index) => (
            <button
              key={index}
              type="button"
              className={`${btn.static ? "static" : ""}`}
              onMouseEnter={() => !btn.static && handleMouseEnter(btn.mouseEnter)}
              onMouseLeave={() => !btn.static && handleMouseLeave()}
              onClick={() => !btn.static && changeView(btn.mouseEnter)}
            >
              {btn.title}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  const renderDestinationView = () => {
    switch (typeDestinationView) {
      case "national":
        return renderDestination({
          btnReturn: true,
          title: tStationsNationals("destinations-and-coverage.title"),
          btns: [
            {
              title: tStationsNationals("destinations-and-coverage.nationals"),
              static: true,
              mouseEnter: "",
            },
          ],
        });
      case "international":
        return renderDestination({
          btnReturn: true,
          title: tStationsNationals("destinations-and-coverage.title"),
          btns: [
            {
              title: tStationsNationals("destinations-and-coverage.america"),
              static: false,
              mouseEnter: "america",
            },
            { title: "Asia", static: false, mouseEnter: "asia" },
            {
              title: tStationsNationals("destinations-and-coverage.europe"),
              static: false,
              mouseEnter: "europe",
            },
          ],
        });
      case "america":
        return renderDestination({
          btnReturn: true,
          title: tStationsNationals("destinations-and-coverage.title"),
          btns: [
            {
              title: tStationsNationals(
                "destinations-and-coverage.north-america"
              ),
              static: false,
              mouseEnter: "northAmerica",
            },
            {
              title: tStationsNationals(
                "destinations-and-coverage.central-america"
              ),
              static: false,
              mouseEnter: "southAmerica",
            },
          ],
        });
      case "northAmerica":
        return renderDestination({
          btnReturn: true,
          title: tStationsNationals("destinations-and-coverage.title"),
          btns: [{ title: "Norteamérica", static: true, mouseEnter: "" }],
        });
      case "southAmerica":
        return renderDestination({
          btnReturn: true,
          title: tStationsNationals("destinations-and-coverage.title"),
          btns: [{ title: "Southamérica", static: true, mouseEnter: "" }],
        });
      case "asia":
        return renderDestination({
          btnReturn: true,
          title: tStationsNationals("destinations-and-coverage.title"),
          btns: [{ title: "Asia", static: true, mouseEnter: "" }],
        });
      case "europe":
        return renderDestination({
          btnReturn: true,
          title: tStationsNationals("destinations-and-coverage.title"),
          btns: [{ title: "Europa", static: true, mouseEnter: "" }],
        });
      default:
        handleMouseLeave();
        return renderDestination({
          btnReturn: false,
          title: tStationsNationals("destinations-and-coverage.title"),
          btns: [
            {
              title: tStationsNationals("destinations-and-coverage.nationals"),
              static: false,
              mouseEnter: "national",
            },
            {
              title: tStationsNationals(
                "destinations-and-coverage.internationals"
              ),
              static: false,
              mouseEnter: "international",
            },
          ],
        });
    }
  };

  const renderStations = () => {
    if (typeDestinationView === "national") {
      return Object.keys(nationalDetails).length ? (
        <div className="national-details">
          <h4>
            {nationalDetails.name} ({nationalDetails.code_text})
          </h4>
          <p>{nationalDetails.address}</p>
          <h4>Teléfono(s):</h4>
          <p>{nationalDetails.phone}</p>
        </div>
      ) : (
        <div className="stations-container">
          <ul>
            {nationalStations.map((station, index) => (
              <li key={station.code || station.code_text || index}>
                <button
                  type="button"
                  className="station-list-item__btn"
                  onClick={() => stationDetail(station)}
                >
                  {station.name} ({station.code_text})
                </button>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return (
      <div className="international-stations-container">
        {Object.entries(internationalStations)
          .filter(([region]) => region === typeDestinationView)
          .flatMap(([_, countries]) =>
            Object.entries(countries).map(([country, stations]) => (
              <div key={country} className="item-section">
                <p className="country-text">{country}</p>
                <ul>
                  {stations.map((st, index) =>
                    st.maps ? (
                      <li key={index}>
                        <a
                          href={st.maps}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="station-list-item__link"
                        >
                          {st.city}
                        </a>
                      </li>
                    ) : (
                      <li key={index}>{st.city}</li>
                    )
                  )}
                </ul>
              </div>
            ))
          )}
      </div>
    );
  };

  return (
    <>
      <div className="coverage-container-home">
        <div className="gray-section-home">
          <div className="bg-black-home" />

          {renderDestinationView()}
        </div>

        <div className="white-section">
          <RenderMap />
          <div
            className={`show-stations ${showStationsOverlay ? "show" : ""}`}
          >
            {renderStations()}
          </div>
        </div>
      </div>
    </>
  );
}

export default DestinationAndCoverage;
